import React, { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { X, Share2, Download, Sparkles, PartyPopper } from "lucide-react";

const CONFETTI = ["#0C8478", "#00D1C2", "#B8873A", "#E0774E", "#14181D"];
const VERSE = "\u201CNot by might, nor by power, but by my Spirit, says the LORD.\u201D \u2014 Zechariah 4:6";

function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = text.split(" ");
  let line = "";
  let cy = y;
  for (const w of words) {
    const test = line ? line + " " + w : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line, x, cy);
      line = w;
      cy += lineHeight;
    } else {
      line = test;
    }
  }
  ctx.fillText(line, x, cy);
  return cy;
}

function drawCard(canvas, { days, label, milestone, money, currency }) {
  const W = 1080, H = 1350;
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#FBFAF7"; ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = "#0C8478"; ctx.fillRect(0, 0, W, 18);
  ctx.textAlign = "center";
  ctx.fillStyle = "#0C8478"; ctx.font = "700 40px Outfit, Arial, sans-serif";
  ctx.fillText("NOT BY WILL", W / 2, 150);
  ctx.fillStyle = "#8A94A3"; ctx.font = "400 26px Outfit, Arial, sans-serif";
  ctx.fillText("By His strength \u00B7 And our faith", W / 2, 196);

  ctx.fillStyle = "#14181D"; ctx.font = "700 340px Outfit, Arial, sans-serif";
  ctx.fillText(String(days), W / 2, 640);
  ctx.fillStyle = "#0C8478"; ctx.font = "600 60px Outfit, Arial, sans-serif";
  ctx.fillText(label, W / 2, 720);

  let cy = 850;
  if (milestone) {
    ctx.fillStyle = "#14181D"; ctx.font = "600 44px Outfit, Arial, sans-serif";
    cy = wrapText(ctx, milestone, W / 2, cy, 900, 58) + 90;
  }
  if (money) {
    ctx.fillStyle = "#B8873A"; ctx.font = "600 44px Outfit, Arial, sans-serif";
    ctx.fillText(`${currency}${money} saved so far`, W / 2, cy);
  }
  ctx.fillStyle = "#5B6472"; ctx.font = "italic 36px Georgia, serif";
  wrapText(ctx, VERSE, W / 2, 1180, 920, 50);
}

export default function CelebrationModal({ open, onClose, days = 0, label = "days reclaimed", milestone = null, milestoneDesc = "", money = 0, currency = "$" }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (open && typeof window !== "undefined" && window.navigator?.vibrate) {
      try { window.navigator.vibrate(30); } catch (e) { /* noop */ }
    }
  }, [open]);

  const data = { days, label, milestone: milestone ? `${milestone} \u00B7 ${milestoneDesc}` : "", money, currency };

  const downloadCard = () => {
    const canvas = canvasRef.current;
    drawCard(canvas, data);
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url; a.download = "not-by-will-progress.png"; a.click();
    toast.success("Saved your progress card.");
  };

  const shareCard = async () => {
    const canvas = canvasRef.current;
    drawCard(canvas, data);
    const text = `I've reclaimed ${days} smoke-free ${days === 1 ? "day" : "days"} with Not By Will \u2014 by His strength. \uD83D\uDE4F`;
    try {
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      const file = new File([blob], "not-by-will-progress.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text, title: "My progress" });
        return;
      }
      if (navigator.share) { await navigator.share({ text, title: "My progress" }); return; }
    } catch (e) {
      if (e && e.name === "AbortError") return;
    }
    downloadCard();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4 bg-brand-navy/50 backdrop-blur-sm"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose} data-testid="celebration-modal"
        >
          {/* confetti */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            {Array.from({ length: 26 }).map((_, i) => (
              <motion.span
                key={i}
                className="absolute top-[-24px] rounded-[2px]"
                style={{ left: `${(i * 3.8) % 100}%`, width: 8, height: 14, background: CONFETTI[i % CONFETTI.length] }}
                initial={{ y: -30, rotate: 0, opacity: 0 }}
                animate={{ y: "105vh", rotate: 360 * (i % 2 ? 1 : -1), opacity: [0, 1, 1, 0.8] }}
                transition={{ duration: 2.4 + (i % 5) * 0.3, delay: (i % 8) * 0.08, ease: "easeIn" }}
              />
            ))}
          </div>

          <motion.div
            onClick={(e) => e.stopPropagation()}
            initial={{ y: 40, scale: 0.96, opacity: 0 }} animate={{ y: 0, scale: 1, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            className="relative w-full max-w-md rounded-3xl bg-sand border border-border shadow-soft-lg overflow-hidden"
          >
            <button data-testid="celebration-close" onClick={onClose} className="absolute top-4 right-4 rounded-full p-1.5 text-stone hover:bg-sage transition-colors z-10">
              <X className="w-5 h-5" />
            </button>

            <div className="p-7 text-center">
              <div className="inline-flex items-center gap-2 rounded-full bg-brand-teal/15 text-brand-teal-dark px-3 py-1.5 text-xs font-semibold mb-5">
                <PartyPopper className="w-3.5 h-3.5" /> {milestone ? "Milestone reached" : "Your progress"}
              </div>

              {/* on-screen card */}
              <div className="rounded-3xl bg-brand-navy text-white px-6 py-8" data-testid="celebration-card">
                <p className="text-[11px] uppercase tracking-[0.2em] text-brand-teal mb-4">Not By Will</p>
                <p className="font-serif text-[5.5rem] leading-none text-white">{days}</p>
                <p className="text-brand-teal font-medium mt-2">{label}</p>
                {milestone && (
                  <p className="text-white/80 text-sm mt-4">
                    <span className="font-semibold text-white">{milestone}</span>{milestoneDesc ? ` — ${milestoneDesc}` : ""}
                  </p>
                )}
                {!!money && money > 0 && (
                  <p className="text-brand-gold-light text-sm mt-3">{currency}{money} saved so far</p>
                )}
                <p className="font-scripture italic text-scripture text-white/70 mt-5">
                  “Not by might, nor by power, but by my Spirit.” — Zechariah 4:6
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-2.5 mt-6">
                <button data-testid="celebration-share" onClick={shareCard}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-terracotta hover:bg-terracotta-soft text-white py-3 text-sm font-semibold transition-colors">
                  <Share2 className="w-4 h-4" /> Share
                </button>
                <button data-testid="celebration-download" onClick={downloadCard}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-border text-olive hover:bg-sage py-3 text-sm font-medium transition-colors">
                  <Download className="w-4 h-4" /> Save image
                </button>
              </div>
              <p className="text-xs text-stone mt-3 flex items-center justify-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-gold" /> Every day is grace. Keep going.
              </p>
            </div>
          </motion.div>

          <canvas ref={canvasRef} className="hidden" aria-hidden="true" />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
